import {
  getStoredFbclid,
  getStoredUtmCampaign,
  getStoredUtmSource,
} from "@/components/feature/utm-source-capture";
import { type AgeGroup, getAgeGroupFromStorage } from "@/lib/age-group/store";
import { getStoredPaidAdChannel } from "@/lib/analytics/paid-ad";
import { AUDIENCE_SURVEY_CAMPAIGN_KEY, AUDIENCE_SURVEY_VERSION } from "@/lib/nps/constants";
import type { AudienceSurveyAnswers } from "@/lib/nps/submit";

export const AUDIENCE_SURVEY_EVENT_SHOWN = "audience_survey_shown";
export const AUDIENCE_SURVEY_EVENT_ACCEPTED = "audience_survey_accepted";
export const AUDIENCE_SURVEY_EVENT_STEP_COMPLETED = "audience_survey_step_completed";
export const AUDIENCE_SURVEY_EVENT_SUBMITTED = "audience_survey_submitted";
export const AUDIENCE_SURVEY_EVENT_COMPLETED = "audience_survey_completed";
export const AUDIENCE_SURVEY_EVENT_DISMISSED = "audience_survey_dismissed";
export const AUDIENCE_SURVEY_EVENT_SAVE_FAILED = "audience_survey_save_failed";

export type NpsAgeGroup = AgeGroup | "unknown";
export type AudienceSurveyPosition =
  | "invite"
  | "audience_who"
  | "audience_where"
  | "audience_role"
  | "feedback"
  | "score"
  | "thankyou";
export type AudienceSurveyStep = "audience" | "feedback";
export type AudienceSurveySaveOperation = "start" | "progress" | "submit";

type CaptureFn = (event: string, properties?: Record<string, unknown>) => void;

export type NpsAnalyticsContext = {
  campaign_key: typeof AUDIENCE_SURVEY_CAMPAIGN_KEY;
  survey_version: typeof AUDIENCE_SURVEY_VERSION;
  age_group: NpsAgeGroup;
  game_slug: string;
  utm_source: string | null;
  utm_campaign: string | null;
  paid_ad_channel: "meta" | null;
  has_fbclid: boolean;
};

export function resolveNpsAgeGroup(
  ageGroup: AgeGroup | null = getAgeGroupFromStorage()
): NpsAgeGroup {
  return ageGroup ?? "unknown";
}

/** Shared non-PII attribution and version properties for survey events. */
export function buildNpsAnalyticsContext(gameSlug: string): NpsAnalyticsContext {
  return {
    campaign_key: AUDIENCE_SURVEY_CAMPAIGN_KEY,
    survey_version: AUDIENCE_SURVEY_VERSION,
    age_group: resolveNpsAgeGroup(),
    game_slug: gameSlug,
    utm_source: getStoredUtmSource(),
    utm_campaign: getStoredUtmCampaign(),
    paid_ad_channel: getStoredPaidAdChannel(),
    has_fbclid: Boolean(getStoredFbclid()),
  };
}

function capture(
  captureFn: CaptureFn | undefined,
  event: string,
  properties: Record<string, unknown>
) {
  if (!captureFn) return;
  try {
    captureFn(event, properties);
  } catch (error) {
    console.error("Error capturing audience survey analytics event", { event, error });
  }
}

function safeAnswers(answers: AudienceSurveyAnswers) {
  return {
    audience: answers.audience,
    setting: answers.setting,
    role: answers.role,
  };
}

export function captureAudienceSurveyShown(
  captureFn: CaptureFn | undefined,
  gameSlug: string
): void {
  capture(captureFn, AUDIENCE_SURVEY_EVENT_SHOWN, buildNpsAnalyticsContext(gameSlug));
}

export function captureAudienceSurveyAccepted(
  captureFn: CaptureFn | undefined,
  gameSlug: string
): void {
  capture(captureFn, AUDIENCE_SURVEY_EVENT_ACCEPTED, buildNpsAnalyticsContext(gameSlug));
}

export function captureAudienceSurveyStepCompleted(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  details:
    | {
        step: "audience";
        answers: AudienceSurveyAnswers;
      }
    | {
        step: "feedback";
        answers: AudienceSurveyAnswers;
        has_feedback: boolean;
      }
): void {
  capture(captureFn, AUDIENCE_SURVEY_EVENT_STEP_COMPLETED, {
    ...buildNpsAnalyticsContext(gameSlug),
    ...safeAnswers(details.answers),
    step: details.step,
    ...(details.step === "feedback" ? { has_feedback: details.has_feedback } : {}),
  });
}

export function captureAudienceSurveySubmitted(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  details: AudienceSurveyAnswers & { score: number }
): void {
  capture(captureFn, AUDIENCE_SURVEY_EVENT_SUBMITTED, {
    ...buildNpsAnalyticsContext(gameSlug),
    ...safeAnswers(details),
    score: details.score,
    has_feedback: Boolean(details.feedback),
  });
}

export function captureAudienceSurveyCompleted(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  details: AudienceSurveyAnswers & { score: number }
): void {
  capture(captureFn, AUDIENCE_SURVEY_EVENT_COMPLETED, {
    ...buildNpsAnalyticsContext(gameSlug),
    ...safeAnswers(details),
    score: details.score,
    has_feedback: Boolean(details.feedback),
  });
}

export function captureAudienceSurveyDismissed(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  position: AudienceSurveyPosition
): void {
  capture(captureFn, AUDIENCE_SURVEY_EVENT_DISMISSED, {
    ...buildNpsAnalyticsContext(gameSlug),
    position,
  });
}

export function captureAudienceSurveySaveFailed(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  operation: AudienceSurveySaveOperation
): void {
  capture(captureFn, AUDIENCE_SURVEY_EVENT_SAVE_FAILED, {
    ...buildNpsAnalyticsContext(gameSlug),
    operation,
  });
}
